import { errorMessageFrom } from "../../../../../../utils/formErrors";
import { ensureOrgEmployees } from "../../../../../../services/useOrgData";
import { getActiveLegalUnitId, getActiveLegalUnit } from "../../../../../../services/globalData";
import { inEntityScope, scopeCounts } from "../../../../../../utils/entityScope";
import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Stack, Avatar, Select, MenuItem, InputBase, IconButton, Menu,
  ListItemIcon, CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { useNavigate } from "react-router-dom";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import Topbar from "../../../../../../component/TopBar";
import MainBox from "../../../../container/MainBox";
import DataTable from "../../../../../../component/DataTable";
import { selectSx, StatusChip } from "../../../../../../component/listing";
import EmptyState from "../../../../../../component/EmptyState";
import OffboardDialog from "../../../../../../component/OffboardDialog";
import { sendEmployeeOnboardingEmail, setEmployeeActive } from "../../../../../../services/employee";
import { getOrgDepartments, getLegalUnitList } from "../../../../../../services/globalData";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont, motion } from "../../../../../../theme";
import { pressable } from "../../../../../../component/pressableProps";
import { startsLater, onNotice, joiningThisMonth } from "../../../../../../utils/peopleFacts";

const statusOf = (e) => {
  if (e?.isActive === false) return "Inactive";
  if (!e?.activated) return "Invited";          // invited, no login yet
  if (!e?.hasBeenOnboarded) return "Activated"; // logged in, profile incomplete
  return "Active";
};
/**
 * Status, in the fixed five.
 *
 * This screen carried its own colour table, which is how it escaped the sweep
 * that removed the brand accent from every other status in the product —
 * "Activated" was rendered in plum, and the rule is that a status never reaches
 * for the accent. Mapping to tones instead means a status badge here means the
 * same thing it means in every other module.
 */
const STATUS_TONE = {
  Active: "success",
  Activated: "info",
  Invited: "warning",
  Inactive: "muted",
};


const initialsOf = (name) =>
  (name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const monthYear = (d) => {
  if (!d) return null;
  const t = new Date(d);
  return Number.isNaN(t.getTime())
    ? null
    : t.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

const dayMonth = (d) => {
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? null : t.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

/**
 * One person, as a card.
 *
 * The book makes cards the default view "because a face and a team are what
 * people scan for" — so the card leads with the avatar and the name, and the
 * things you would sort or export by are left to the list.
 *
 * A future starter is the one card that says something different: their first
 * day has not happened, so "Joined" would be a lie and the date they arrive is
 * the useful fact instead.
 */
const PersonCard = ({ person, name, role, meta, onClick, index = 0 }) => {
  const upcoming = startsLater(person);
  // The book alternates the avatar between plum and plum-dark so a grid of
  // faces does not read as one flat block of the same colour.
  const avatarBg = index % 2 === 0 ? tokens.primary : tokens.primaryHover;
  return (
    <Box
      {...pressable(onClick, name)}
      sx={{
        border: `1px solid ${tokens.border}`,
        borderRadius: "10px",
        padding: "20px",
        background: tokens.card,
        cursor: "pointer",
        transition: `border-color 120ms ${motion.easing.standard}, background 120ms ${motion.easing.standard}`,
        "&:hover": { borderColor: tokens.borderStrong, background: tokens.surfaceSubtle },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: "14px" }}>
        <Avatar
          src={person?.profileImage || undefined}
          sx={{ width: "2.75rem", height: "2.75rem", bgcolor: avatarBg, color: tokens.onPrimary, fontSize: "0.9375rem", fontWeight: 600 }}
        >
          {initialsOf(name)}
        </Avatar>
        {upcoming && (
          <Box
            component="span"
            sx={{
              background: tokens.infoTint, color: tokens.infoText, borderRadius: "999px",
              padding: "3px 10px", fontSize: "0.6875rem", fontWeight: 600, whiteSpace: "nowrap",
            }}
          >
            Starts {dayMonth(person.joiningDate)}
          </Box>
        )}
      </Stack>
      <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mb: "2px" }}>{name}</Box>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "12px" }}>{role}</Box>
      <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, lineHeight: 1.6 }}>{meta}</Box>
    </Box>
  );
};

/**
 * Cards or list.
 *
 * A segmented control rather than two buttons: the two options are one choice
 * with one answer, and a pair of buttons would leave the current state to be
 * inferred from which one looks pressed.
 */
const ViewToggle = ({ view, onChange }) => (
  <Stack
    direction="row"
    role="group"
    aria-label="View"
    sx={{ border: `1px solid ${tokens.borderStrong}`, borderRadius: "8px", padding: "3px", flexShrink: 0 }}
  >
    {[
      { key: "cards", label: "Cards", Icon: GridViewOutlinedIcon },
      { key: "list", label: "List", Icon: ViewListOutlinedIcon },
    ].map(({ key, label, Icon }) => {
      const active = view === key;
      return (
        <Box
          key={key}
          component="button"
          type="button"
          aria-pressed={active}
          onClick={() => onChange(key)}
          sx={{
            display: "flex", alignItems: "center", gap: "7px",
            border: "none", cursor: "pointer", fontFamily: "inherit",
            borderRadius: "6px", padding: "7px 14px",
            fontSize: "0.8125rem", fontWeight: active ? 600 : 500,
            background: active ? tokens.primary : "transparent",
            color: active ? tokens.card : tokens.primary,
            "&:hover": { background: active ? tokens.primaryHover : tokens.surfaceSubtle },
          }}
        >
          <Icon sx={{ fontSize: "0.9375rem" }} />
          {label}
        </Box>
      );
    })}
  </Stack>
);

/**
 * The People list, as column definitions.
 *
 * Declared rather than rendered inline so the header, the widths, the alignment
 * and the loading skeleton all agree without anyone restating them — which is
 * what went wrong when every screen wrote its own table.
 */
const PEOPLE_COLUMNS = ({ deptName, entityNames, openMenu }) => [
  {
    key: "employee",
    label: "Employee",
    render: (e) => (
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Avatar
          src={e.profileImage || undefined}
          sx={{ width: "2.25rem", height: "2.25rem", bgcolor: tokens.primary, color: tokens.onPrimary, fontSize: "0.8125rem", fontWeight: 600 }}
        >
          {`${e.fname?.[0] || ""}${e.lname?.[0] || ""}`.toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontWeight: 600, color: tokens.text }}>{`${e.fname || ""} ${e.lname || ""}`.trim()}</Box>
          {e.email && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{e.email}</Box>}
        </Box>
      </Stack>
    ),
  },
  { key: "aliasId", label: "Employee ID", render: (e) => e.aliasId || "—" },
  { key: "department", label: "Department", render: (e) => deptName(e.department) },
  { key: "designation", label: "Designation", render: (e) => e.designation || "—" },
  {
    key: "legalUnit",
    label: "Legal entity",
    cellSx: { color: tokens.textMuted },
    render: (e) => entityNames(e.legalUnit),
  },
  {
    key: "employmentType",
    label: "Type",
    // Neutral, not the brand accent: employment type is a category, and the
    // accent is reserved for the one thing that matters most on a screen.
    render: (e) => (e.employmentType ? <StatusChip label={e.employmentType} tone="muted" /> : "—"),
  },
  {
    key: "status",
    label: "Status",
    render: (e) => {
      const status = statusOf(e);
      return <StatusChip label={status} tone={STATUS_TONE[status]} />;
    },
  },
  {
    key: "actions",
    label: "",
    align: "right",
    width: "4rem",
    render: (e) => (
      <IconButton aria-label="More actions" size="small" onClick={(ev) => openMenu(ev, e)} sx={{ color: tokens.textMuted }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
    ),
  },
];

export default function EmployeesTable() {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  // Which entity's people to show. Defaults to the one you are working in —
  // switching entity should change who is listed, or two companies under one
  // organisation appear to share their staff.
  const [entityScope, setEntityScope] = useState("entity");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [offboardRow, setOffboardRow] = useState(null); // employee pending offboard
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  /**
   * Cards or list.
   *
   * The book is firm that this "changes the rendering, never the query" — so it
   * is the only thing this state touches. Search, the filters and the selection
   * all live above it and are untouched by switching, which is what lets someone
   * flip to the list to sort, flip back, and still be where they were.
   */
  const [view, setView] = useState("cards");

  const departments = getOrgDepartments() || [];
  const entities = getLegalUnitList() || [];
  const deptName = (id) => departments.find((d) => d?._id === id)?.name || "—";
  const entityNames = (ids) =>
    (Array.isArray(ids) ? ids : [])
      .map((id) => entities.find((u) => u?._id === id)?.name)
      .filter(Boolean)
      .join(", ") || "—";

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      // Writes invalidate the cached directory, so a load() after one still
      // reaches the server; a load() after a plain revisit does not.
      const list = await ensureOrgEmployees();
      // The whole organisation is kept here and narrowed to the entity in the
      // filter below, so changing scope costs no request.
      setData(Array.isArray(list) ? [...list].reverse() : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };

  const resendInvite = async (row) => {
    closeMenu();
    try {
      const res = await sendEmployeeOnboardingEmail(row._id);
      if (res?.success !== false) notify.success(`Invite resent to ${row.fname || "employee"}`);
      else notify.error(errorMessageFrom(res, "Could not resend invite"));
    } catch (err) {
      notify.error(err?.message || "Could not resend invite");
    }
  };

  const reactivate = async (row) => {
    closeMenu();
    try {
      const res = await setEmployeeActive(row._id, true);
      if (res?.success !== false) { notify.success(`${row.fname || "Employee"} reactivated`); load(); }
      else notify.error(errorMessageFrom(res, "Could not reactivate"));
    } catch (err) {
      notify.error(err?.message || "Could not reactivate");
    }
  };

  const stats = useMemo(() => {
    const s = { total: data.length, active: 0, invited: 0, joining: 0, onNotice: 0 };
    data.forEach((e) => {
      const st = statusOf(e);
      if (st === "Active") s.active += 1;
      else if (st === "Invited") s.invited += 1;

      // The book's header line: who is arriving, and who is leaving. Both are
      // counted from dates rather than status, because somebody serving notice
      // is still Active and still on the payroll.
      if (joiningThisMonth(e)) s.joining += 1;
      if (onNotice(e)) s.onNotice += 1;
    });
    return s;
  }, [data]);

  // Said as one line rather than a row of tiles: none of it is actionable, and
  // the book gives this screen its counts as context under the title.
  const subtitle = [
    `${stats.active} active`,
    stats.joining > 0 && `${stats.joining} joining this month`,
    stats.onNotice > 0 && `${stats.onNotice} on notice`,
  ].filter(Boolean).join(" · ");

  const activeEntityId = getActiveLegalUnitId();
  const activeEntityName = getActiveLegalUnit()?.name;
  // The rule and the counts live in utils/entityScope so the next entity-scoped
  // list uses this one rather than forming a second opinion.
  const counts = useMemo(() => scopeCounts(data, activeEntityId), [data, activeEntityId]);

    /**
   * Why the list is empty, when the answer is the entity rather than the search.
   *
   * "No matches — try adjusting your search or filters" sends somebody to
   * rewrite a query that was never the problem: they are looking at one legal
   * entity and the people are in another. Naming the entity, and saying where
   * the rest are, is the difference between a dead end and a next step.
   */
  const emptyDescription = () => {
    if (data.length === 0) return "Add your first person to get started.";
    const searching = search.trim() || deptFilter !== "all" || statusFilter !== "all";
    if (!searching && entityScope === "entity" && activeEntityId) {
      return counts.unassigned > 0
        ? `Nobody is assigned to ${activeEntityName || "this entity"} yet. ${counts.unassigned} ${counts.unassigned === 1 ? "person is" : "people are"} not in any entity — switch to Unassigned to place them.`
        : `Nobody is assigned to ${activeEntityName || "this entity"} yet. Switch to All entities to see everyone in the organisation.`;
    }
    if (!searching && entityScope === "unassigned") {
      return "Everyone is assigned to a legal entity.";
    }
    return "Try adjusting your search or filters.";
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((e) => {
      if (!inEntityScope(e, entityScope, activeEntityId)) return false;
      const matchesSearch = !q ||
        `${e.fname || ""} ${e.lname || ""}`.toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q) ||
        (e.aliasId || "").toLowerCase().includes(q);
      const matchesDept = deptFilter === "all" || e.department === deptFilter;
      const matchesStatus = statusFilter === "all" || statusOf(e) === statusFilter;
      return matchesSearch && matchesDept && matchesStatus;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search, deptFilter, statusFilter, entityScope, activeEntityId]);

  // Selection is scoped to the currently-filtered rows.
  const allFilteredSelected = filtered.length > 0 && filtered.every((e) => selected.has(e._id));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach((e) => next.delete(e._id));
      else filtered.forEach((e) => next.add(e._id));
      return next;
    });
  };
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectedRows = data.filter((e) => selected.has(e._id));

  const exportCsv = () => {
    const rows = (selectedRows.length ? selectedRows : filtered).map((e) => ({
      Name: `${e.fname || ""} ${e.lname || ""}`.trim(),
      EmployeeID: e.aliasId || "",
      Email: e.email || "",
      Phone: e.phone || "",
      Department: deptName(e.department),
      Designation: e.designation || "",
      Type: e.employmentType || "",
      Status: statusOf(e),
    }));
    if (!rows.length) { notify.error("Nothing to export"); return; }
    const cfg = mkConfig({ useKeysAsHeaders: true, filename: "employees" });
    download(cfg)(generateCsv(cfg)(rows));
    notify.success(`Exported ${rows.length} employee(s)`);
  };

  const runBulk = async (fn, verb) => {
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const e of selectedRows) {
      try {
        const res = await fn(e);
        res?.success === false ? fail++ : ok++;
      } catch { fail++; }
    }
    setBulkBusy(false);
    if (ok) notify.success(`${verb} ${ok} employee(s)`);
    if (fail) notify.error(`${fail} failed`);
    clearSelection();
    load();
  };


  return (
    <Box>
      <Topbar MainHeading={"People"} />

      <MainBox sx={{ padding: "1.75rem" }}>
        {/* Title, the counts as context, then the view toggle and the actions. */}
        <Stack direction="row" alignItems="center" sx={{ gap: "14px", mb: "20px", flexWrap: "wrap" }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontFamily: displayFont, fontSize: "1.5rem", fontWeight: 700, color: tokens.text, mb: "4px" }}>
              People
            </Box>
            <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>{subtitle}</Box>
          </Box>
          <Box sx={{ flex: 1 }} />
          <ViewToggle view={view} onChange={setView} />
          <BtnSecondary onClick={exportCsv} sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}>
            Export
          </BtnSecondary>
          <BtnPrimary to="onboarding" sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}>
            Add person
          </BtnPrimary>
        </Stack>

        {/* toolbar */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, flexWrap: "wrap" }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ border: `1px solid ${tokens.borderStrong}`, borderRadius: "8px", px: 1.5, height: "2.75rem", background: tokens.card, minWidth: "16rem" }}>
              <SearchIcon sx={{ fontSize: "1.125rem", color: tokens.textMuted }} />
              <InputBase placeholder="Search name, email or ID" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ fontSize: "0.875rem", flex: 1 }} />
            </Stack>
            <Select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} size="small" sx={selectSx}>
              <MenuItem value="all">All departments</MenuItem>
              {departments.map((d) => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
            </Select>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="small" sx={selectSx}>
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Invited">Invited</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
            {/* Only worth showing when there is more than one answer: an
                organisation with a single entity and nobody unplaced has
                nothing to choose between. */}
            {(activeEntityId || counts.unassigned > 0) && (
              <Select value={entityScope} onChange={(e) => setEntityScope(e.target.value)} size="small" sx={selectSx}>
                {activeEntityId && (
                  <MenuItem value="entity">
                    {activeEntityName || "This entity"} ({counts.inEntity})
                  </MenuItem>
                )}
                <MenuItem value="unassigned">Unassigned ({counts.unassigned})</MenuItem>
                <MenuItem value="all">All entities ({counts.all})</MenuItem>
              </Select>
            )}
          </Stack>
        </Stack>

        {/* Bulk action bar */}
        {selectedRows.length > 0 && (
          <Stack direction="row" alignItems="center" justifyContent="space-between"
            sx={{ background: `${tokens.accentTint}`, border: `1px solid ${tokens.accent}33`, borderRadius: "10px", p: "0.75rem 1rem", mb: "1rem" }}>
            <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text }}>
              {selectedRows.length} selected
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <BtnSecondary onClick={exportCsv} disabled={bulkBusy} sx={{ borderRadius: "8px", fontSize: "0.8125rem" }}>
                <FileDownloadOutlinedIcon sx={{ fontSize: "1rem", mr: 0.5 }} /> Export CSV
              </BtnSecondary>
              <BtnSecondary onClick={() => runBulk((e) => sendEmployeeOnboardingEmail(e._id), "Resent invite to")} disabled={bulkBusy} sx={{ borderRadius: "8px", fontSize: "0.8125rem" }}>
                Resend invite
              </BtnSecondary>
              <BtnSecondary onClick={() => runBulk((e) => setEmployeeActive(e._id, false), "Deactivated")} disabled={bulkBusy}
                sx={{ height: "2.25rem", px: "1rem", borderRadius: "8px", fontSize: "0.8125rem", color: tokens.danger, borderColor: tokens.dangerBorder }}>
                {bulkBusy ? <CircularProgress size={16} /> : "Deactivate"}
              </BtnSecondary>
              <IconButton size="small" onClick={clearSelection} sx={{ color: tokens.textMuted }}>
                <BlockIcon sx={{ display: "none" }} />
                <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, px: 0.5 }}>Clear</Box>
              </IconButton>
            </Stack>
          </Stack>
        )}

        {/* Error and empty are the same answer whichever way you are looking,
            so they sit outside the view. Loading is not: the list shows its own
            header with skeleton rows, cards show skeleton cards. */}
        {error ? (
          <EmptyState variant="error" title="Couldn't load employees" onRetry={load} />
        ) : view === "cards" ? (
          loading ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
                gap: "16px",
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <Box key={i} className="pl-skeleton" sx={{ height: "9.5rem", borderRadius: "10px" }} />
              ))}
            </Box>
          ) : filtered.length === 0 ? (
            <EmptyState
              title={data.length === 0 ? "No people yet" : "No matches"}
              description={emptyDescription()}
            />
          ) : (
          /* Cards are the default: a face and a team are what people scan for.
             The same `filtered` list the table uses, rendered differently — the
             toggle must never change the query. */
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: "16px",
            }}
          >
            {filtered.map((e, i) => {
              const name = `${e.fname || ""} ${e.lname || ""}`.trim() || "Unnamed";
              const where = [deptName(e.department), entityNames(e.legalUnit)]
                .filter((x) => x && x !== "—")
                .join(" · ");
              const joined = monthYear(e.joiningDate);
              return (
                <PersonCard
                  key={e._id}
                  person={e}
                  name={name}
                  role={e.designation || "—"}
                  meta={
                    <>
                      {where || "—"}
                      {joined && !startsLater(e) && <><br />Joined {joined}</>}
                    </>
                  }
                  onClick={() => navigate(`/app/employee-details/${e._id}`)}
                  index={i}
                />
              );
            })}
          </Box>
          )
        ) : (
          <DataTable
            rows={filtered}
            loading={loading}
            empty={{
              title: data.length === 0 ? "No people yet" : "No matches",
              description: data.length === 0
                ? "Add your first person to get started."
                : emptyDescription(),
            }}
            onRowClick={(e) => navigate(`/app/employee-details/${e._id}`)}
            selected={selected}
            onToggleRow={toggleSelect}
            onToggleAll={toggleSelectAll}
            columns={PEOPLE_COLUMNS({ deptName, entityNames, openMenu })}
          />
        )}
      </MainBox>

      {/* Row actions menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
        slotProps={{ paper: { sx: { borderRadius: "10px", minWidth: "12rem" } } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}>
        {menuRow && statusOf(menuRow) === "Invited" && (
          <MenuItem onClick={() => resendInvite(menuRow)} sx={{ fontSize: "0.875rem" }}>
            <ListItemIcon sx={{ minWidth: "2rem", color: tokens.textMuted }}><MailOutlineIcon fontSize="small" /></ListItemIcon>
            Resend invite
          </MenuItem>
        )}
        {menuRow && statusOf(menuRow) === "Inactive" ? (
          <MenuItem onClick={() => reactivate(menuRow)} sx={{ fontSize: "0.875rem", color: tokens.successText }}>
            <ListItemIcon sx={{ minWidth: "2rem", color: tokens.successText }}><CheckCircleOutlineIcon fontSize="small" /></ListItemIcon>
            Reactivate
          </MenuItem>
        ) : menuRow && (
          <MenuItem onClick={() => { const r = menuRow; closeMenu(); setOffboardRow(r); }} sx={{ fontSize: "0.875rem", color: tokens.danger }}>
            <ListItemIcon sx={{ minWidth: "2rem", color: tokens.danger }}><BlockIcon fontSize="small" /></ListItemIcon>
            Offboard
          </MenuItem>
        )}
      </Menu>

      <OffboardDialog
        open={Boolean(offboardRow)}
        employee={offboardRow}
        onClose={() => setOffboardRow(null)}
        onDone={load}
      />
    </Box>
  );
}
